// frontend/src/pages/SalesPipelinePage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { formatDistanceToNow } from "date-fns";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Cell,
} from "recharts";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Filter,
  Hourglass,
  Send,
  Trophy,
} from "lucide-react";
import API from "../utils/api";
import { toast } from "react-toastify";

/** KPI cards: tinted icon circle + title/hint + large value (see mockup). */
const PIPELINE_STAGES = [
  {
    key: "sent",
    label: "Proposals sent",
    hint: "Sent Proposals this month",
    valueKey: "proposals_sent",
    Icon: Send,
    color: "#3B82F6",
    bg: { light: "rgba(59, 130, 246, 0.14)", dark: "rgba(59, 130, 246, 0.22)" },
  },
  {
    key: "pending",
    label: "Pending",
    hint: "Sent, awaiting customer (now)",
    valueKey: "pending_proposals",
    Icon: Hourglass,
    color: "#F59E0B",
    bg: { light: "rgba(245, 158, 11, 0.16)", dark: "rgba(245, 158, 11, 0.24)" },
  },
  {
    key: "won",
    label: "Closed won",
    hint: "Leads closed this month",
    valueKey: "closed_won_leads",
    Icon: Trophy,
    color: "#10B981",
    bg: { light: "rgba(16, 185, 129, 0.14)", dark: "rgba(16, 185, 129, 0.22)" },
  },
];

const OUTCOME_COLORS = ["#3B82F6", "#A78BFA"];

/**
 * Modern floating card: hairline border + soft layered shadow on white (light mode),
 * subtle slate surface in dark mode. Matches sidebar/topbar visual language.
 */
function floatingCardSx(theme) {
  const isLight = theme.palette.mode === "light";
  return {
    borderRadius: "16px",
    bgcolor: isLight ? "#ffffff" : "rgba(30,41,59,0.88)",
    border: "1px solid",
    borderColor: isLight ? "#f1f5f9" : "rgba(255,255,255,0.08)",
    boxShadow: isLight
      ? "0 1px 2px rgba(15,23,42,0.04), 0 6px 24px rgba(15,23,42,0.04)"
      : "0 8px 24px rgba(0,0,0,0.55)",
    transition: "box-shadow 180ms ease, transform 180ms ease",
  };
}

function headerCardSx(theme) {
  return {
    ...floatingCardSx(theme),
    p: 3,
    mb: 3,
  };
}

/** Tiny uppercase section label that sits above each row of cards (PIPELINE / PERFORMANCE / ACTIVITY). */
function SectionLabel({ children, sx }) {
  return (
    <Typography
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: (t) =>
          t.palette.mode === "light" ? "#94a3b8" : "rgba(255,255,255,0.55)",
        mb: 1.25,
        mt: 0.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/** Soft tinted pill for proposal status — replaces filled MUI chip color="success" etc. */
function statusChipSx(theme, status) {
  const isLight = theme.palette.mode === "light";
  const palette = theme.palette;
  const map = {
    accepted: { main: palette.success.main, fg: palette.success.dark },
    sent: { main: palette.info.main, fg: palette.info.dark },
    declined: { main: palette.error.main, fg: palette.error.dark },
    withdrawn: { main: palette.warning.main, fg: palette.warning.dark },
    draft: { main: palette.grey[500], fg: palette.text.secondary },
  };
  const c = map[status] || map.draft;
  return {
    bgcolor: alpha(c.main, isLight ? 0.12 : 0.22),
    color: isLight ? c.fg : c.main,
    fontWeight: 600,
    height: 24,
    border: "none",
    textTransform: "capitalize",
    "& .MuiChip-label": { px: 1.25 },
  };
}

function initials(name) {
  if (!name || name === "—") return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/** ~10 rows tall; extra rows scroll inside the section. */
const PIPELINE_SECTION_SCROLL_MAX_PX = 520;

/** Recent Activity: ~6 items visible; additional items scroll in the panel. */
const RECENT_ACTIVITY_SCROLL_MAX_PX = 440;

/** Team Performance: list capped at 5 reps; scroll if content overflows the viewport. */
const TEAM_PERFORMANCE_SCROLL_MAX_PX = 320;

/** No row lines — spacing only (leaderboard & at a glance tables). */
const plainLeaderboardTableSx = {
  "& .MuiTableCell-root": {
    borderBottom: "none",
    py: 1.35,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    borderBottom: "none",
    color: "text.secondary",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    pb: 1,
    pt: 0,
  },
};

export default function SalesPipelinePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const tz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [territoryId, setTerritoryId] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const leaderboardTableSx = useMemo(
    () => ({
      ...plainLeaderboardTableSx,
      "& .MuiTableBody-root .MuiTableRow-root:hover": {
        bgcolor:
          theme.palette.mode === "light"
            ? "#F2F7FF"
            : "rgba(59, 130, 246, 0.12)",
      },
    }),
    [theme.palette.mode]
  );

  const fetchPulse = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
      });
      if (tz) params.set("tz", tz);
      if (territoryId) params.set("territory", territoryId);
      const res = await API.get(`performance/sales-pulse/?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to load sales pipeline.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, tz, territoryId]);

  useEffect(() => {
    fetchPulse();
  }, [fetchPulse]);

  useEffect(() => {
    if (!data?.meta?.can_filter_territory) return;
    const sid = data.meta.selected_territory_id;
    if (sid != null) {
      setTerritoryId(String(sid));
    } else {
      setTerritoryId("");
    }
  }, [data]);

  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const barData = useMemo(() => {
    if (!data?.outcomes) return [];
    return [
      { name: "Accepted", value: data.outcomes.accepted ?? 0 },
      { name: "Declined", value: data.outcomes.declined ?? 0 },
    ];
  }, [data]);

  const periodLabel = data?.period?.label || "—";

  const showTerritoryFilter =
    data?.meta?.can_filter_territory && (data?.territories?.length ?? 0) > 0;

  const territoryButtonLabel = useMemo(() => {
    if (!territoryId) return "Territory";
    const t = data?.territories?.find((x) => String(x.id) === String(territoryId));
    return t?.name || "Territory";
  }, [territoryId, data?.territories]);

  const filterPopoverOpen = Boolean(filterAnchorEl);

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header — floating card with brand-tinted icon circle + month nav */}
        <Card sx={headerCardSx(theme)}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: (t) =>
                    alpha(
                      t.palette.primary.main,
                      t.palette.mode === "light" ? 0.1 : 0.18
                    ),
                  color: "primary.main",
                }}
              >
                <BarChart3 size={28} strokeWidth={1.75} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
                >
                  Sales Pipeline
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track sales funnel, monitor proposals, and company leaderboard.
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-tour="sales-pipeline-month-nav"
            >
              <IconButton
                size="small"
                onClick={() => shiftMonth(-1)}
                aria-label="Previous month"
                sx={{ color: "text.primary" }}
              >
                <ChevronLeft size={20} strokeWidth={1.75} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 700,
                  minWidth: 160,
                  textAlign: "center",
                  letterSpacing: "-0.01em",
                }}
              >
                {loading ? "…" : periodLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() => shiftMonth(1)}
                aria-label="Next month"
                sx={{ color: "text.primary" }}
              >
                <ChevronRight size={20} strokeWidth={1.75} />
              </IconButton>
            </Stack>
          </Stack>
        </Card>

        {loading && !data ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : data ? (
          <>
            {/* Section 1 — Pipeline (KPI cards). Section label + territory filter share this row. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.25,
                mt: 0.5,
                minHeight: 32,
              }}
            >
              <SectionLabel sx={{ mb: 0, mt: 0 }}>Pipeline</SectionLabel>
              {showTerritoryFilter && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Filter size={16} strokeWidth={1.75} />}
                    aria-haspopup="true"
                    aria-expanded={filterPopoverOpen ? "true" : undefined}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    sx={{
                      borderRadius: "10px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "primary.main",
                      px: 1.75,
                      py: 0.5,
                      textTransform: "none",
                      fontWeight: 600,
                      maxWidth: "100%",
                      "& .MuiButton-startIcon": { flexShrink: 0 },
                      "&:hover": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      noWrap
                      sx={{ maxWidth: { xs: 140, sm: 220 } }}
                    >
                      {territoryButtonLabel}
                    </Typography>
                  </Button>
                  <Popover
                    open={filterPopoverOpen}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 0.75,
                          minWidth: 260,
                          maxWidth: 360,
                          borderRadius: 2,
                          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Territory
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5 }}
                      >
                        Filter metrics by territory
                      </Typography>
                    </Box>
                    <List
                      dense
                      disablePadding
                      sx={{ maxHeight: 280, overflow: "auto", pb: 1 }}
                    >
                      <ListItemButton
                        selected={!territoryId}
                        onClick={() => {
                          setTerritoryId("");
                          setFilterAnchorEl(null);
                        }}
                      >
                        <ListItemText primary="All territories" />
                      </ListItemButton>
                      {data.territories.map((t) => (
                        <ListItemButton
                          key={t.id}
                          selected={String(territoryId) === String(t.id)}
                          onClick={() => {
                            setTerritoryId(String(t.id));
                            setFilterAnchorEl(null);
                          }}
                        >
                          <ListItemText primary={t.name} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Popover>
                </>
              )}
            </Box>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
              gap={3}
              mb={3}
              data-tour="sales-pipeline-kpis"
            >
              {PIPELINE_STAGES.map((stage) => {
                const { Icon, color, bg, label, hint, valueKey } = stage;
                const iconBg = theme.palette.mode === "dark" ? bg.dark : bg.light;
                const val = data.pipeline?.[valueKey] ?? 0;
                return (
                  <Card key={stage.key} sx={floatingCardSx(theme)}>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            bgcolor: iconBg,
                            color,
                          }}
                        >
                          <Icon size={20} strokeWidth={1.75} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
                          >
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {hint}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: { xs: "2.75rem", md: "3.25rem" },
                          fontWeight: 800,
                          letterSpacing: "-0.035em",
                          lineHeight: 1,
                          color: "text.primary",
                        }}
                      >
                        {val}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {/* -------------------------------------------------------Sections 2 + 3 — Performance */}
            <SectionLabel>Performance</SectionLabel>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }}
              gap={3}
              mb={3}
              data-tour="sales-pipeline-performance"
            >
              <Card sx={floatingCardSx(theme)}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }} mb={0.5}>
                    Proposal outcomes
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Accepted vs Declined
                  </Typography>
                  <Box sx={{ width: "100%", height: isMobile ? 220 : 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={barData}
                        layout="vertical"
                        margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
                        barCategoryGap="18%"
                      >
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: theme.palette.text.secondary,
                            fontSize: 12,
                          }}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={76}
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: theme.palette.text.primary,
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        />
                        <RechartsTooltip
                          cursor={{ fill: "transparent" }}
                          contentStyle={{
                            borderRadius: 10,
                            border: "none",
                            boxShadow: theme.shadows[4],
                            fontSize: 13,
                          }}
                        />
                        <Bar dataKey="value" radius={[0, 8, 8, 0]} maxBarSize={48}>
                          {barData.map((_, i) => (
                            <Cell key={i} fill={OUTCOME_COLORS[i % OUTCOME_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={floatingCardSx(theme)}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }} mb={0.5}>
                    Team Performance
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Individual Metrics
                  </Typography>
                  <TableContainer
                    sx={{
                      maxHeight: TEAM_PERFORMANCE_SCROLL_MAX_PX,
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader size="small" sx={leaderboardTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Rep
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Closed
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Sent
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.leaderboard || []).slice(0, 5).map((row) => (
                          <TableRow key={row.user_id} hover>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "action.selected",
                                    color: "primary.main",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {initials(row.display_name)}
                                </Box>
                                <Typography variant="body2">{row.display_name}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700}>{row.closed_won}</Typography>
                            </TableCell>
                            <TableCell align="right">{row.proposals_sent}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            {/* Sections 4 + 5 — Activity */}
            <SectionLabel>Activity</SectionLabel>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }}
              gap={3}
              alignItems="flex-start"
            >
              <Card sx={floatingCardSx(theme)}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }} mb={0.5}>
                    Recent Activity
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Most Recent Team Updates
                  </Typography>
                  {(data.recent_activity || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No activity this month.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: RECENT_ACTIVITY_SCROLL_MAX_PX,
                        overflowY: "auto",
                        pr: 0.5,
                        mr: -0.5,
                      }}
                    >
                      <Stack spacing={1.25} component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                        {data.recent_activity.map((ev) => (
                          <Box
                            key={ev.id}
                            component="li"
                            sx={{
                              py: 1,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 0.25 }}>
                              <strong>{ev.actor_label}</strong> — {ev.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(ev.timestamp), {
                                addSuffix: true,
                              })}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card sx={floatingCardSx(theme)}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }} mb={0.5}>
                    Proposals At a Glance
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Latest Proposal Statuses
                  </Typography>
                  <TableContainer
                    sx={{
                      maxHeight: PIPELINE_SECTION_SCROLL_MAX_PX,
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader size="small" sx={leaderboardTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Lead
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.95)"
                                  : "background.paper",
                            }}
                          >
                            Age
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.proposals_glance || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">
                                No sent, accepted, withdrawn, or declined proposals to show.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (data.proposals_glance || []).map((row) => (
                            <TableRow
                              key={row.proposal_id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                navigateToLeadDetail(navigate, row.lead_id, {
                                  pathname: "/sales-pipeline",
                                  label: "Sales Pipeline",
                                })
                              }
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: "underline",
                                    textUnderlineOffset: "2px",
                                  }}
                                >
                                  {row.lead_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.status}
                                  sx={statusChipSx(theme, row.status)}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {row.age_days != null ? `${row.age_days}d` : "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
